import type {
  AnalyticsConfig,
  GetInteractionAggregatesReqFiltersV2Item,
} from '@/api/generated/model';
import { chartTypeMap } from '@/api/requests/types/charts.type';
import { AIFI_PROFICIENCY_CHART_BREAKDOWN } from '@/domains/pages/ai-proficiency/utils/aifi-aggregates-api';
import { mapAiProficiencyChartGranularity } from '@/domains/pages/ai-proficiency/utils/api-payloads';
import { AI_PROFICIENCY_ROLLING_WINDOW } from '@/domains/pages/ai-proficiency/utils/rolling-trend-query';
import type { TrendQueryConfig } from '@/domains/pages/shared/trend-query';
import type { NebulyPageKey } from '@/domains/pages/types';
import {
  buildSegmentGroupBy,
  segmentKindLabel,
  type SegmentKind,
} from '@/domains/pages/shared/segment-kind';

export type { SegmentKind };
export { buildSegmentGroupBy, segmentKindLabel };

export function pageKeyToSegmentKind(pageKey: NebulyPageKey): SegmentKind | null {
  if (pageKey === 'ai-proficiency-team') return 'team';
  if (pageKey === 'ai-proficiency-market') return 'market';
  if (pageKey === 'ai-proficiency-tenure') return 'tenure';
  if (pageKey === 'team' || pageKey === 'market' || pageKey === 'tenure') return pageKey;
  return null;
}

export function buildSegmentFilterV2(kind: SegmentKind, name: string): GetInteractionAggregatesReqFiltersV2Item {
  return buildSegmentFilterV2Values(kind, [name]);
}

export function buildSegmentFilterV2Values(
  kind: SegmentKind,
  names: string[],
): GetInteractionAggregatesReqFiltersV2Item {
  return {
    kind,
    values: names,
    is_exclude: false,
  } as GetInteractionAggregatesReqFiltersV2Item;
}

export function buildSegmentScopeFiltersV2(
  kind: SegmentKind,
  segmentNames: string | string[],
): GetInteractionAggregatesReqFiltersV2Item[] {
  const names = Array.isArray(segmentNames) ? segmentNames : [segmentNames];
  return [buildSegmentFilterV2Values(kind, names)];
}

export function buildSegmentScoreTrendChartConfig(
  trendQuery: TrendQueryConfig,
  segmentKind: SegmentKind,
): AnalyticsConfig {
  return {
    report_type: 'analytics',
    chart_type: chartTypeMap.line,
    variables: ['aifi_score'],
    filters: [],
    special_filters: [],
    breakdown: segmentKind,
    granularity: mapAiProficiencyChartGranularity(trendQuery.granularity),
    visualization_type: 'linear',
    time_range: trendQuery.time_range,
    rolling_window: AI_PROFICIENCY_ROLLING_WINDOW,
  };
}

export function buildSegmentLevelMixChartConfig(
  trendQuery: TrendQueryConfig,
  segmentKind: SegmentKind,
  segmentNames: string | string[],
): AnalyticsConfig {
  const names = Array.isArray(segmentNames) ? segmentNames : [segmentNames];

  return {
    report_type: 'analytics',
    chart_type: chartTypeMap.line,
    variables: ['users'],
    filters: [
      {
        column: segmentKind,
        values: names,
        is_exclude: false,
      },
    ],
    special_filters: [],
    breakdown: AIFI_PROFICIENCY_CHART_BREAKDOWN,
    granularity: mapAiProficiencyChartGranularity(trendQuery.granularity),
    visualization_type: 'linear',
    time_range: trendQuery.time_range,
    rolling_window: AI_PROFICIENCY_ROLLING_WINDOW,
  };
}
